// TimeScale (round 2): the hours over the lanes, as the sample writes them: the first hour with its
// "a.m.", noon and one o'clock named, the rest bare ("8 a.m.", "9", "10", "11", "12 p.m.", "1 p.m.",
// "2"), a tick at each hour, and "Now 10:30" in red where now falls (an hour too close to it gives way).
// On the board it is the grid's first row and its first cell, over the names, is the corner (BoardCorner:
// Group and Filter). Anywhere else (a timesheet's day) it is drawn `alone`: the track only, as wide as
// what holds it, inside a TimeSpanProvider. Too narrow for every hour's label (a timesheet's day, a
// phone), it thins them to every 2nd, 3rd, 4th, 6th or 12th hour, on the clock's own hours so noon
// stays named, and every label it keeps stands clear of the next; the ticks stay at every hour. On the
// board, as the track scrolls sideways under the corner, a label the corner or the board's edge would
// cut is drawn whole or not at all (QA-765): it slides in beside the edge while its hour still shows,
// and goes once its hour has (edge-fit.ts).
// Reads only --scale-*.
import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { applyFit, clearFit, hourFit, trackView } from '../edge-fit.ts';
import { spanHours, spanPct, useTimeSpan } from '../time-span.tsx';
import './TimeScale.css';

/** An hour as the scale writes it. */
export function hourWord(minute: number, first: boolean): string {
  const h = Math.floor(minute / 60) % 24;
  const h12 = ((h + 11) % 12) + 1;
  if (h === 12) return '12 p.m.';
  if (first || h === 13 || h === 0) return `${h12} ${h < 12 ? 'a.m.' : 'p.m.'}`;
  return String(h12);
}

/** Now as the scale writes it: "10:30", "9" on the hour. */
export function nowWord(minute: number): string {
  const h = Math.floor(minute / 60) % 24;
  const m = minute % 60;
  const h12 = ((h + 11) % 12) + 1;
  return m ? `${h12}:${String(m).padStart(2, '0')}` : String(h12);
}

/** A label as drawn along the track: its hour (minutes from midnight) and its ends, in pixels. */
export interface HourBox {
  minute: number;
  left: number;
  right: number;
}

/** Every how many hours a label goes, fewest first. Each divides 12, so noon is always one. */
const STEPS = [1, 2, 3, 4, 6, 12];

/**
 * Which hours keep their label, so that none runs into the next (with `gap` between): every hour if
 * they fit, else the first and every 2nd, 3rd… hour of the clock, the fewest steps that fit. The first
 * (which says a.m. or p.m.) gives way to a label right beside it, except that noon or midnight beside
 * it, which say so themselves, takes its place.
 */
export function shownHours(boxes: readonly HourBox[], gap: number): Set<number> {
  const clear = (a: HourBox, b: HourBox) => a.right + gap <= b.left;
  let keep: HourBox[] = [];
  for (const step of STEPS) {
    keep = boxes.filter((b, i) => i === 0 || (b.minute / 60) % step === 0);
    const [first, next] = keep;
    if (first && next && !clear(first, next))
      keep = next.minute % 720 === 0 ? keep.slice(1) : [first, ...keep.slice(2)];
    if (keep.every((b, i) => i === 0 || clear(keep[i - 1]!, b))) break;
  }
  return new Set(keep.map((b) => b.minute));
}

export interface TimeScaleProps {
  /** What sits over the names: BoardCorner. */
  corner?: ReactNode;
  /** Not a row of a Board's grid: the track alone, with no names column. */
  alone?: boolean;
}

export function TimeScale({ corner, alone = false }: TimeScaleProps) {
  const span = useTimeSpan();
  const hours = spanHours(span);
  const showNow = span.now !== null && span.now >= span.from && span.now <= span.to;
  // the labels that fit (shownHours), then an hour whose label the now label would cover gives way, as
  // the sample does: measured, since how wide an hour is depends on what holds the scale (the board at
  // 1440, a timesheet's narrow day, a phone)
  const track = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = track.current;
    if (!el) return;
    const board = el.closest<HTMLElement>('.fl-board');
    // on the board: each label shown whole within what shows of the track, or not at all
    const edges = () => {
      if (!board) return;
      const view = trackView(board);
      const t = el.getBoundingClientRect();
      for (const h of el.querySelectorAll<HTMLElement>('.fl-scale__hour:not([hidden]), .fl-scale__now')) {
        clearFit(h);
        const r = h.getBoundingClientRect();
        const anchor = t.left + (parseFloat(h.style.insetInlineStart) / 100) * t.width;
        applyFit(h, hourFit(r, anchor, view, r.height / 4));
      }
    };
    const fit = () => {
      const labels = [...el.querySelectorAll<HTMLElement>('.fl-scale__hour')];
      for (const h of labels) {
        h.hidden = false;
        clearFit(h);
      }
      const rects = labels.map((h) => h.getBoundingClientRect());
      // half a line between labels, as between an hour and now
      const gap = (rects[0]?.height ?? 0) / 2;
      const kept = shownHours(
        rects.map((r, i) => ({ minute: Number(labels[i]!.dataset.minute), left: r.left, right: r.right })),
        gap,
      );
      const now = el.querySelector<HTMLElement>('.fl-scale__now')?.getBoundingClientRect();
      labels.forEach((h, i) => {
        const r = rects[i]!;
        // thinned out, or covered by the now label or closer than half a line to it
        h.hidden =
          !kept.has(Number(h.dataset.minute)) ||
          (!!now && r.right + gap > now.left && r.left - gap < now.right);
      });
      edges();
    };
    fit();
    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(edges);
    };
    board?.addEventListener('scroll', onScroll, { passive: true });
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(fit) : null;
    ro?.observe(el);
    if (board) ro?.observe(board);
    return () => {
      cancelAnimationFrame(frame);
      board?.removeEventListener('scroll', onScroll);
      ro?.disconnect();
    };
  }, [span.from, span.to, span.now, span.density]);
  return (
    <div className="fl-scale" data-density={span.density} data-alone={alone ? '' : undefined}>
      {alone ? null : <div className="fl-scale__corner">{corner}</div>}
      <div className="fl-scale__track" aria-hidden="true" ref={track}>
        {hours.map((m) => (
          <span
            key={`t${m}`}
            className="fl-scale__tick"
            style={{ insetInlineStart: `${spanPct(span, m)}%` }}
          />
        ))}
        {hours.map((m, i) => (
          <span
            key={`l${m}`}
            className="fl-scale__hour"
            data-minute={m}
            data-edge={i === 0 ? 'first' : i === hours.length - 1 ? 'last' : undefined}
            style={{ insetInlineStart: `${spanPct(span, m)}%` }}
          >
            {hourWord(m, i === 0)}
          </span>
        ))}
        {showNow ? (
          <span className="fl-scale__now" style={{ insetInlineStart: `${spanPct(span, span.now!)}%` }}>
            Now {nowWord(span.now!)}
          </span>
        ) : null}
      </div>
    </div>
  );
}
